import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AccountScopeSelect from "@/components/AccountScopeSelect";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  SquaresFour, ListDashes, ChartLineUp, Gear, Plus, SignOut, TrendUp,
  SquaresFour as _SquaresFour, Tag,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard", bar: true },
  { to: "/transactions", label: "Transactions", icon: ListDashes, testid: "nav-transactions", bar: true },
  { to: "/categories", label: "Categories", icon: Tag, testid: "nav-categories" },
  { to: "/forecast", label: "Forecast", icon: ChartLineUp, testid: "nav-forecast", bar: true },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings", bar: true },
];

// Four destinations is what fits a phone bar beside the action button without
// the labels turning into abbreviations. Categories is reached from the user
// menu and from the transactions page, which is where you actually want it.
const BAR_NAV = NAV.filter((item) => item.bar);

const BottomNav = () => (
  <nav
    aria-label="Primary"
    data-testid="bottom-nav"
    className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-black/80 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden"
  >
    {BAR_NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`${item.testid}-bar`}
        className={({ isActive }) =>
          `flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
            isActive ? "text-income" : "text-muted-foreground"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <item.icon size={21} weight={isActive ? "fill" : "regular"} />
            {item.label}
          </>
        )}
      </NavLink>
    ))}
    {/* The action button sits in the bar's last slot so it stops floating over
        the content it is meant to act on. */}
    <span className="w-[4.5rem] shrink-0" aria-hidden="true" />
  </nav>
);

const NavItems = () => (
  <nav className="flex flex-col gap-1">
    {NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={item.testid}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
            isActive
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          }`
        }
      >
        <item.icon size={18} weight="bold" />
        {item.label}
      </NavLink>
    ))}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2 px-1">
    <div className="w-7 h-7 bg-income flex items-center justify-center rounded-sm">
      <TrendUp size={16} weight="bold" className="text-black" />
    </div>
    <span className="font-heading font-extrabold tracking-tight">Fin<span className="text-income">Flo</span></span>
  </div>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-dvh bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-border p-4 sticky top-0 h-screen">
        <div className="mb-8 mt-1"><Brand /></div>
        <NavItems />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 lg:px-8 h-16 border-b border-border bg-black/60 backdrop-blur-xl">
          <span className="shrink-0 lg:hidden"><Brand /></span>

          {/* min-w-0 lets the account selector give up width on the narrowest
              phones instead of crowding the brand. */}
          <div className="ml-auto flex min-w-0 items-center justify-end gap-3">
            <AccountScopeSelect />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 focus:outline-none">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">{initials}</div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                <DropdownMenuLabel className="truncate">
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/categories")} data-testid="menu-categories" className="lg:hidden">
                  <Tag size={16} className="mr-2" /> Categories
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Gear size={16} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-expense focus:text-expense">
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:p-8 lg:pb-28">{children}</main>

        <BottomNav />
      </div>

      <Button
        type="button"
        aria-label="Add transaction"
        data-testid="floating-add-transaction"
        onClick={() => navigate("/transactions?new=1")}
        className="fixed bottom-[env(safe-area-inset-bottom)] right-0 z-50 h-14 w-[4.5rem] rounded-none bg-income p-0 text-black hover:bg-income/90 lg:bottom-8 lg:right-8 lg:h-14 lg:w-auto lg:gap-2 lg:rounded-full lg:px-5 lg:ring-1 lg:ring-inset lg:ring-white/20"
      >
        <Plus size={22} weight="bold" />
        <span className="hidden font-heading text-sm font-bold lg:inline">New transaction</span>
      </Button>
    </div>
  );
}
